import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PageHeader, StaffBadge, EventStatusBadge, ProgressLine, Empty } from "@/components/atlas/Shared";
import { GoldButton, GhostButton, NativeSelect } from "@/components/atlas/Fields";
import { useFetch } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { dateParts, fmtRange } from "@/lib/format";
import { downloadCsv } from "@/lib/api";

const EMPTY = { q: "", client: "", location: "", staffing: "", status: "", date_from: "", date_to: "" };

export default function EventsList() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const { data: events } = useFetch("/events", f);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: typeof v === "string" ? v : v.target.value }));
  const exportCsv = () => downloadCsv().catch((e) => toast.error(errorText(t, e)));

  return (
    <div>
      <PageHeader eyebrow="Atlas" title={t("ev.title")} actions={<>
        <GhostButton data-testid="events-export-csv-btn" onClick={exportCsv}><Download className="w-4 h-4" />{t("ev.export")}</GhostButton>
        <GoldButton data-testid="events-new-btn" onClick={() => navigate("/admin/events/new")}><Plus className="w-4 h-4" />{t("ev.new")}</GoldButton>
      </>} />
      <div className="card-lux p-4 mb-6 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="events-filters">
        <div className="relative col-span-2 md:col-span-1 xl:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-neutral-400" />
          <Input data-testid="events-search-input" value={f.q} onChange={set("q")} placeholder={t("c.search")} className="pl-9 h-10 rounded-none" />
        </div>
        <Input data-testid="events-filter-client" value={f.client} onChange={set("client")} placeholder={t("ev.filterClient")} className="h-10 rounded-none" />
        <Input data-testid="events-filter-location" value={f.location} onChange={set("location")} placeholder={t("ev.filterLocation")} className="h-10 rounded-none" />
        <NativeSelect testid="events-filter-staffing" value={f.staffing} onChange={set("staffing")}>
          <option value="">{t("ev.staffing")} · {t("c.all")}</option>
          {["vacant", "partial", "full"].map((s) => <option key={s} value={s}>{t(`st.${s}`)}</option>)}
        </NativeSelect>
        <NativeSelect testid="events-filter-status" value={f.status} onChange={set("status")}>
          <option value="">{t("ev.lifecycle")} · {t("c.all")}</option>
          {["draft", "published", "cancelled"].map((s) => <option key={s} value={s}>{t(`es.${s}`)}</option>)}
        </NativeSelect>
        <div className="flex gap-2 col-span-2 md:col-span-4 xl:col-span-1 xl:col-start-1 xl:row-start-2">
          <Input data-testid="events-filter-from" type="date" value={f.date_from} onChange={set("date_from")} className="h-10 rounded-none" title={t("ev.from")} />
        </div>
        <Input data-testid="events-filter-to" type="date" value={f.date_to} onChange={set("date_to")} className="h-10 rounded-none xl:row-start-2" title={t("ev.to")} />
        <button data-testid="events-filter-reset" onClick={() => setF(EMPTY)} className="text-xs text-neutral-500 hover:text-black underline underline-offset-4 justify-self-start xl:row-start-2">{t("ev.reset")}</button>
      </div>
      {events && events.length === 0 && <Empty text={t("c.none")} testid="events-empty" />}
      <div className="space-y-3" data-testid="events-list">
        {(events || []).map((e, i) => {
          const d = dateParts(e.date, lang);
          return (
            <button key={e.id} onClick={() => navigate(`/admin/events/${e.id}`)} data-testid={`event-row-${e.id}`}
              className="press w-full text-left card-lux hover:border-[#C5A059]/50 grid grid-cols-[64px_1fr] md:grid-cols-[72px_1.6fr_1fr_220px_130px] items-center gap-4 md:gap-6 p-4 md:p-5 rise"
              style={{ animationDelay: `${Math.min(i, 10) * 30}ms` }}>
              <div className="text-center border-r border-black/[0.06] pr-4">
                <div className="eyebrow">{d.month}</div>
                <div className="font-display text-3xl leading-none mt-1">{d.day}</div>
                <div className="text-[10px] text-neutral-400 uppercase mt-1">{d.weekday}</div>
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{e.name}</div>
                <div className="text-xs text-neutral-500 mt-1 truncate">{e.client} · {e.location} · <span className="mono">{fmtRange(e.start_time, e.end_time, t)}</span></div>
                <div className="md:hidden mt-3 flex items-center gap-2 flex-wrap">
                  <StaffBadge status={e.stats.staffing_status} />
                  <EventStatusBadge status={e.status} />
                </div>
                <div className="md:hidden mt-3"><ProgressLine filled={e.stats.filled} required={e.stats.required} /></div>
              </div>
              <div className="hidden md:flex flex-wrap gap-1.5">
                {e.stats.slots.slice(0, 4).map((s) => (
                  <span key={s.slot_id} className={`text-[10px] mono px-1.5 py-0.5 border ${s.status === "full" ? "border-emerald-200 text-emerald-700" : s.status === "partial" ? "border-amber-200 text-amber-700" : "border-red-200 text-red-700"}`}>{s.filled}/{s.required}</span>
                ))}
                {e.stats.slots.length > 4 && <span className="text-[10px] mono px-1.5 py-0.5 text-neutral-500">+{e.stats.slots.length - 4}</span>}
              </div>
              <div className="hidden md:block"><ProgressLine filled={e.stats.filled} required={e.stats.required} testid={`event-progress-${e.id}`} /></div>
              <div className="hidden md:flex flex-col items-end gap-1.5">
                <StaffBadge status={e.stats.staffing_status} />
                <EventStatusBadge status={e.status} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
